<template>
  <a-modal
    title="关键词回复内容"
    :visible="visible"
    @cancel="handleCancel"
    :width="700"
    class="response-modal"
    :footer="null"
    :maskClosable="true"
    :keyboard="true"
    :destroyOnClose="true"
    :getPopupContainer="getPopupContainer"
  >
    <div class="modal-content">
      <a-spin :spinning="loading">
        <div v-if="keyword" class="response-header mb-3">
          <div class="keyword-info">
            <span class="label">关键词：</span>
            <a-tag color="blue">{{ keyword.content }}</a-tag>
          </div>
        </div>
        
        <div v-if="responses.length > 0" class="response-list">
          <a-list itemLayout="vertical">
            <a-list-item v-for="(response, index) in responses" :key="response._id">
              <template #extra>
                <div class="response-actions">
                  <a-switch
                    :checked="response.isActive"
                    :loading="loadingId === response._id"
                    @change="(checked) => toggleResponseStatus(response._id, checked)"
                    class="mb-2"
                  />
                  <a-button type="link" @click="handleEditResponse(response)" class="mb-2">编辑</a-button>
                  <a-popconfirm
                    title="确定要删除此回复吗？"
                    ok-text="确定"
                    cancel-text="取消"
                    @confirm="handleDeleteResponse(response._id)"
                  >
                    <a-button type="link" danger>删除</a-button>
                  </a-popconfirm>
                </div>
              </template>
              
              <a-list-item-meta>
                <template #title>
                  <div class="response-type">
                    <a-tag :color="getResponseTypeColor(response.responseType)">
                      {{ getResponseTypeLabel(response.responseType) }}
                    </a-tag>
                  </div>
                </template>
              </a-list-item-meta>
              
              <div class="response-content">
                {{ response.content }}
              </div>
              
              <div v-if="response.url" class="response-url mt-2">
                <span class="label">链接：</span>
                <a :href="response.url" target="_blank">{{ response.url }}</a>
              </div>
            </a-list-item>
          </a-list>
        </div>
        <div v-else class="empty-state">
          <a-empty description="暂无回复内容" />
        </div>
        
        <div class="modal-footer">
          <a-button key="cancel" @click="handleCancel">关闭</a-button>
          <a-button 
            key="add" 
            type="primary" 
            @click="showAddResponseModal"
            v-if="responses.length === 0"
          >
            <template #icon><plus-outlined /></template>
            添加回复内容
          </a-button>
        </div>
      </a-spin>
    </div>
  </a-modal>
  
  <!-- 응답 등록 모달 컴포넌트 사용 -->
  <response-register-modal
    :visible="isResponseModalVisible"
    @update:visible="isResponseModalVisible = $event"
    :is-editing="isEditingResponse"
    :keyword-id="keyword?._id"
    :response="currentResponse"
    :is-submitting="isSubmitting"
    @cancel="handleResponseModalCancel"
    @create="handleCreateResponse"
    @update="handleUpdateResponse"
    @submit="handleResponseSubmit"
  />
</template>

<script>
import { ref, watch } from 'vue';
import { PlusOutlined } from '@ant-design/icons-vue';
import ResponseRegisterModal from './ResponseRegisterModal.vue';

export default {
  name: 'KeywordReplyModal',
  components: {
    PlusOutlined,
    ResponseRegisterModal
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    keyword: {
      type: Object,
      default: null
    },
    responses: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    loadingId: {
      type: String,
      default: null
    }
  },
  emits: [
    'update:visible', 
    'toggle-status', 
    'delete-response', 
    'create-response',
    'update-response',
    'preview'
  ],
  setup(props, { emit }) {
    // 응답 등록 모달 상태
    const isResponseModalVisible = ref(false);
    const isEditingResponse = ref(false);
    const isSubmitting = ref(false);
    const currentResponse = ref(null);
    
    // 응답 타입 라벨 가져오기
    const getResponseTypeLabel = (responseType) => {
      const types = {
        TEXT: '文本',
        IMAGE: '图片',
        LINK: '链接',
        CARD: '卡片'
      };
      return types[responseType] || '文本';
    };
    
    // 응답 타입 색상 가져오기
    const getResponseTypeColor = (responseType) => {
      const colors = {
        TEXT: 'blue',
        IMAGE: 'green',
        LINK: 'purple',
        CARD: 'orange'
      };
      return colors[responseType] || 'blue';
    };

    // 모달 취소 핸들러
    const handleCancel = () => {
      emit('update:visible', false);
      emit('preview');  // 모달이 닫힐 때 preview 실행
    };
    
    // 응답 상태 토글
    const toggleResponseStatus = (id, isActive) => {
      emit('toggle-status', { id, isActive });
    };
    
    // 응답 삭제 핸들러
    const handleDeleteResponse = (id) => {
      emit('delete-response', id);
    };
    
    // 응답 편집 핸들러
    const handleEditResponse = (response) => {
      isEditingResponse.value = true;
      currentResponse.value = { ...response };
      isResponseModalVisible.value = true;
    };
    
    // 응답 추가 모달 표시 핸들러
    const showAddResponseModal = () => {
      isEditingResponse.value = false;
      currentResponse.value = {
        _id: null,
        responseType: 'TEXT',
        content: '',
        url: '',
        isActive: true
      };
      isResponseModalVisible.value = true;
    };
    
    // 응답 모달 취소 핸들러
    const handleResponseModalCancel = () => {
      isResponseModalVisible.value = false;
    };
    
    // 응답 생성 핸들러
    const handleCreateResponse = (data) => {
      isSubmitting.value = true;
      emit('create-response', data);
    };
    
    // 응답 업데이트 핸들러
    const handleUpdateResponse = (data) => {
      isSubmitting.value = true;
      emit('update-response', data);
    };
    
    // 응답 제출 완료 핸들러
    const handleResponseSubmit = () => {
      isSubmitting.value = false;
      isResponseModalVisible.value = false;
    };
    
    // 모달 컨테이너 설정
    const getPopupContainer = () => {
      return document.body;
    };
    
    return {
      // 응답 목록 관련
      getResponseTypeLabel,
      getResponseTypeColor,
      handleCancel,
      toggleResponseStatus,
      handleDeleteResponse,
      handleEditResponse,
      getPopupContainer,
      
      // 응답 등록 모달 관련
      isResponseModalVisible,
      isEditingResponse,
      isSubmitting,
      currentResponse,
      showAddResponseModal,
      handleResponseModalCancel,
      handleCreateResponse,
      handleUpdateResponse,
      handleResponseSubmit
    };
  }
};
</script>

<style lang="scss" scoped>
// 응답 모달 스타일
.response-modal {
  :deep(.ant-modal-content) {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    
    .ant-modal-header {
      border-radius: 8px 8px 0 0;
      background-color: #f8f9fa;
      padding: 16px 24px;
      border-bottom: none;
      margin-bottom: 0;
      position: relative;
      
      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 1px;
        background: linear-gradient(to right, rgba(240, 240, 240, 0), rgba(240, 240, 240, 1) 20%, rgba(240, 240, 240, 1) 80%, rgba(240, 240, 240, 0));
      }
      
      .ant-modal-title {
        font-size: 18px;
        font-weight: 600;
        color: #1f2937;
      }
    }
    
    .ant-modal-body {
      padding: 0;
    }
    
    .ant-modal-close {
      top: 12px;
      right: 12px;
    }
  }
  
  .modal-content {
    border: none;
    padding: 24px;
    position: relative;
    
    &::before {
      display: none;
    }
    
    .modal-footer {
      margin-top: 24px;
      display: flex;
      justify-content: flex-end;
      gap: 12px;
       
      .ant-btn {
        height: 36px;
        font-size: 14px;
        border-radius: 4px;
        padding: 0 16px;
         
        &.ant-btn-primary {
          font-weight: 500;
        }
      }
    }
  }
}

.response-header {
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
  
  .keyword-info {
    .label {
      color: #4b5563;
      font-weight: 500;
      margin-right: 8px;
    }
  }
}

.response-list {
  :deep(.ant-list-item) {
    padding: 16px;
    border-radius: 8px;
    border: 1px solid #f0f0f0;
    margin-bottom: 16px;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    .ant-list-item-meta-title {
      margin-bottom: 8px;
    }
  }
}

.response-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.response-type {
  margin-bottom: 8px;
}

.response-content {
  white-space: pre-wrap;
  word-break: break-word;
}

.response-url {
  font-size: 14px;
  
  .label {
    color: rgba(0, 0, 0, 0.45);
    margin-right: 4px;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 0;
}

.mb-3 {
  margin-bottom: 16px;
}

.mt-2 {
  margin-top: 8px;
}

.mb-2 {
  margin-bottom: 8px;
}

// 확인 팝업 버튼을 가로로 배치하기 위한 강화된 스타일
:deep(.ant-popconfirm-buttons) {
  display: flex !important;
  flex-direction: row !important;
  justify-content: flex-end !important;
  
  .ant-btn {
    margin-left: 8px !important;
    margin-top: 0 !important;
    
    &:first-child {
      margin-left: 0 !important;
    }
  }
}
</style>
